/**
 * Minor settlement panel — v0M9 (2026-07-27) + v0M9-followup.2/.4 (2026-07-27).
 *
 * Modal for one minor Settlement. Tabbed layout:
 *   - Status: pillage / repair state + Repair / Cancel Repair actions.
 *   - Build: structure roster (2-slot cap per MECHANICS §3 v0M9 amendment).
 *   - Recruit: Garrison recruit (defensive-only; Armies are recruited at
 *     Province Capitals per MECHANICS §3.1).
 *
 * Rebuild discipline: DOM structure built ONCE per settlementId change;
 * sync() mutates existing element text/disabled states only. Structural
 * rebuilds of the build / recruit queue viz happen only when the queue's
 * shape (length + ordered name list) changes.
 */

import { injectMenuStyles } from './menu-styles';
import { loadStructures } from '../../data/structures';
import { loadKnowledge } from '../../data/knowledge';
import { loadCohorts } from '../../data/cohorts';
import { loadDials, getDialNumber } from '../../data/dials';
import type { Command } from '../../sim/commands';
import type {
  FactionId,
  GameState,
  Province,
  Settlement,
  SettlementId,
} from '../../sim/state';
import {
  MINOR_SETTLEMENT_PILLAGE_DAYS,
  MINOR_SETTLEMENT_REPAIR_DAYS,
  MINOR_SETTLEMENT_REPAIR_GOLD_COST,
  MINOR_SETTLEMENT_STRUCTURE_CAP,
} from '../../sim/state';

const STRUCTURES = loadStructures();
const KNOWLEDGE = loadKnowledge();
const COHORTS = loadCohorts();
const MINOR_PANEL_DIALS = loadDials();
const GARRISON_COHORT_TYPE = 'Garrison';

// v0M9-followup.3 dial fallback (may not exist on xlsx yet).
function safeDialNumber(name: string, fallback: number): number {
  try { return getDialNumber(MINOR_PANEL_DIALS, name); } catch { return fallback; }
}
const MINOR_GARRISON_MAX = Math.max(0, Math.floor(safeDialNumber('minor_settlement_garrison_max', 1)));

/**
 * Preferred head for the build list — mirrors settlement-panel PREFERRED
 * order. Any additional xlsx structures append after.
 */
const PREFERRED_BUILD_ORDER_HEAD: readonly string[] = [
  'Blacksmith', 'Forester', 'Farm', 'Monastery', 'Stables', 'Market',
];
const BUILD_ORDER: readonly string[] = (() => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const name of PREFERRED_BUILD_ORDER_HEAD) {
    if (STRUCTURES[name]) { out.push(name); seen.add(name); }
  }
  for (const name of Object.keys(STRUCTURES)) {
    if (!seen.has(name)) out.push(name);
  }
  return out;
})();

const UNLOCKS_STRUCTURE_RE = /Unlocks\s+Structure:\s*(.+)/i;
const STRUCTURE_UNLOCK_KNOWLEDGE: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const knowledgeName of Object.keys(KNOWLEDGE)) {
    const spec = KNOWLEDGE[knowledgeName]!;
    const m = UNLOCKS_STRUCTURE_RE.exec(spec.effects);
    if (m && m[1]) map.set(m[1].trim(), knowledgeName);
  }
  return map;
})();

export interface MinorSettlementPanelCallbacks {
  onIssue(cmd: Command): void;
  onClose(): void;
}

export interface MinorSettlementPanelHandle {
  sync(state: GameState, openSettlementId: SettlementId | null, playerFactionId: FactionId | null): void;
  destroy(): void;
}

export function mountMinorSettlementPanel(
  root: HTMLElement,
  callbacks: MinorSettlementPanelCallbacks,
): MinorSettlementPanelHandle {
  injectMenuStyles();

  const backdrop = document.createElement('div');
  backdrop.className = 'su-city-panel-backdrop';
  backdrop.style.display = 'none';

  const panel = document.createElement('div');
  panel.className = 'su-city-panel';
  backdrop.appendChild(panel);

  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) callbacks.onClose();
  });

  // Header ---------------------------------------------------------
  const header = document.createElement('div');
  header.className = 'su-city-panel-header';
  const title = document.createElement('h2');
  title.className = 'su-city-panel-title';
  header.appendChild(title);
  const closeBtn = document.createElement('button');
  closeBtn.className = 'su-city-panel-close';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close (Esc)';
  closeBtn.addEventListener('click', () => callbacks.onClose());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // Kind + facts rows (always visible) -----------------------------
  const kindRow = document.createElement('div');
  kindRow.className = 'su-city-panel-note';
  kindRow.style.padding = '0 16px 4px';
  panel.appendChild(kindRow);
  const factsRow = document.createElement('div');
  factsRow.className = 'su-city-panel-note';
  factsRow.style.padding = '0 16px 8px';
  panel.appendChild(factsRow);

  // Tabs row -------------------------------------------------------
  const tabs = document.createElement('div');
  tabs.className = 'su-city-panel-tabs';
  const statusTabBtn = document.createElement('button');
  statusTabBtn.className = 'su-city-panel-tab su-active';
  statusTabBtn.textContent = 'Status';
  const buildTabBtn = document.createElement('button');
  buildTabBtn.className = 'su-city-panel-tab';
  buildTabBtn.textContent = 'Build';
  const recruitTabBtn = document.createElement('button');
  recruitTabBtn.className = 'su-city-panel-tab';
  recruitTabBtn.textContent = 'Recruit';
  tabs.appendChild(statusTabBtn);
  tabs.appendChild(buildTabBtn);
  tabs.appendChild(recruitTabBtn);
  panel.appendChild(tabs);

  const body = document.createElement('div');
  body.className = 'su-city-panel-body';
  panel.appendChild(body);

  // ---------- STATUS TAB ------------------------------------------
  const statusTab = document.createElement('div');
  body.appendChild(statusTab);
  const statusCard = document.createElement('div');
  statusCard.style.padding = '10px 12px';
  statusCard.style.borderRadius = '4px';
  statusCard.style.marginBottom = '10px';
  statusCard.style.background = 'rgba(255,255,255,0.06)';
  const statusLabel = document.createElement('div');
  statusLabel.style.fontWeight = 'bold';
  statusLabel.style.marginBottom = '6px';
  statusCard.appendChild(statusLabel);
  const barOuter = document.createElement('div');
  barOuter.style.height = '8px';
  barOuter.style.background = 'rgba(255,255,255,0.15)';
  barOuter.style.borderRadius = '4px';
  barOuter.style.overflow = 'hidden';
  barOuter.style.marginBottom = '4px';
  barOuter.style.display = 'none';
  const barFill = document.createElement('div');
  barFill.style.height = '100%';
  barFill.style.width = '0%';
  barFill.style.transition = 'width 200ms linear';
  barOuter.appendChild(barFill);
  statusCard.appendChild(barOuter);
  const barLabel = document.createElement('div');
  barLabel.style.fontSize = '11px';
  barLabel.style.opacity = '0.85';
  barLabel.style.display = 'none';
  statusCard.appendChild(barLabel);
  statusTab.appendChild(statusCard);

  const actionRow = document.createElement('div');
  actionRow.style.display = 'flex';
  actionRow.style.gap = '8px';
  actionRow.style.alignItems = 'center';
  const repairBtn = document.createElement('button');
  repairBtn.className = 'su-city-panel-action';
  repairBtn.textContent = 'Repair';
  actionRow.appendChild(repairBtn);
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'su-city-panel-action';
  cancelBtn.textContent = 'Cancel Repair';
  cancelBtn.style.display = 'none';
  actionRow.appendChild(cancelBtn);
  const actionNote = document.createElement('div');
  actionNote.className = 'su-city-panel-note';
  actionNote.style.opacity = '0.85';
  actionNote.style.fontSize = '11px';
  actionRow.appendChild(actionNote);
  statusTab.appendChild(actionRow);

  // ---------- BUILD TAB -------------------------------------------
  const buildTab = document.createElement('div');
  buildTab.style.display = 'none';
  body.appendChild(buildTab);
  const buildCapNote = document.createElement('div');
  buildCapNote.className = 'su-city-panel-note';
  buildCapNote.style.fontWeight = 'bold';
  buildCapNote.style.marginBottom = '8px';
  buildTab.appendChild(buildCapNote);
  interface BuildRow {
    container: HTMLElement;
    button: HTMLButtonElement;
    countBadge: HTMLElement;
    name: string;
  }
  const buildRows: BuildRow[] = [];
  for (const name of BUILD_ORDER) {
    const spec = STRUCTURES[name];
    if (!spec) continue;
    const row = document.createElement('div');
    row.className = 'su-city-panel-row';
    const countBadge = document.createElement('div');
    countBadge.textContent = '0×';
    countBadge.style.minWidth = '28px';
    countBadge.style.textAlign = 'right';
    countBadge.style.marginRight = '8px';
    countBadge.style.fontSize = '12px';
    countBadge.style.opacity = '0.5';
    countBadge.style.flexShrink = '0';
    row.appendChild(countBadge);
    const nm = document.createElement('div');
    nm.className = 'su-city-panel-item-name';
    nm.textContent = `${name} → ${spec.produces} +${spec.ratePerSec}/s`;
    row.appendChild(nm);
    const cost = document.createElement('div');
    cost.className = 'su-city-panel-item-cost';
    cost.textContent = `${spec.goldCost}G ${spec.buildTimeSec}s`;
    row.appendChild(cost);
    const btn = document.createElement('button');
    btn.className = 'su-city-panel-action';
    btn.textContent = 'Build';
    row.appendChild(btn);
    buildTab.appendChild(row);
    buildRows.push({ container: row, button: btn, countBadge, name });
  }
  const buildQueueHeader = document.createElement('div');
  buildQueueHeader.className = 'su-city-panel-note';
  buildQueueHeader.style.marginTop = '4px';
  buildQueueHeader.style.fontWeight = 'bold';
  buildQueueHeader.textContent = 'In Progress';
  buildQueueHeader.style.display = 'none';
  buildTab.appendChild(buildQueueHeader);
  const buildQueueList = document.createElement('div');
  buildQueueList.style.marginBottom = '8px';
  buildTab.appendChild(buildQueueList);

  interface QueueVizRow {
    container: HTMLElement;
    label: HTMLElement;
    fill: HTMLElement;
    structureName: string;
    buildTimeSec: number;
  }
  let buildQueueVizRows: QueueVizRow[] = [];
  let lastBuildQueueHash = '';

  // ---------- RECRUIT TAB -----------------------------------------
  const recruitTab = document.createElement('div');
  recruitTab.style.display = 'none';
  body.appendChild(recruitTab);
  const recruitNoteTop = document.createElement('div');
  recruitNoteTop.className = 'su-city-panel-note';
  recruitNoteTop.style.marginBottom = '8px';
  recruitNoteTop.style.fontSize = '11px';
  recruitNoteTop.style.opacity = '0.75';
  recruitNoteTop.textContent = 'Minor settlements can host a garrison for defence. Armies are recruited at Province Capitals.';
  recruitTab.appendChild(recruitNoteTop);
  const garrisonRow = document.createElement('div');
  garrisonRow.className = 'su-city-panel-row';
  garrisonRow.style.marginBottom = '8px';
  const garrisonName = document.createElement('div');
  garrisonName.className = 'su-city-panel-item-name';
  garrisonName.textContent = 'Garrison';
  garrisonRow.appendChild(garrisonName);
  const garrisonCount = document.createElement('div');
  garrisonCount.className = 'su-city-panel-item-cost';
  garrisonCount.textContent = `0 / ${MINOR_GARRISON_MAX}`;
  garrisonRow.appendChild(garrisonCount);
  const garrisonBtn = document.createElement('button');
  garrisonBtn.className = 'su-city-panel-action';
  garrisonBtn.textContent = 'Recruit';
  garrisonRow.appendChild(garrisonBtn);
  recruitTab.appendChild(garrisonRow);
  const garrisonCostNote = document.createElement('div');
  garrisonCostNote.className = 'su-city-panel-note';
  garrisonCostNote.style.fontSize = '11px';
  garrisonCostNote.style.opacity = '0.75';
  recruitTab.appendChild(garrisonCostNote);
  const recruitQueueHeader = document.createElement('div');
  recruitQueueHeader.className = 'su-city-panel-note';
  recruitQueueHeader.style.marginTop = '10px';
  recruitQueueHeader.style.fontWeight = 'bold';
  recruitQueueHeader.textContent = 'In Progress';
  recruitQueueHeader.style.display = 'none';
  recruitTab.appendChild(recruitQueueHeader);
  const recruitQueueList = document.createElement('div');
  recruitTab.appendChild(recruitQueueList);
  let recruitQueueVizRows: QueueVizRow[] = [];
  let lastRecruitQueueHash = '';

  root.appendChild(backdrop);

  // Tabs plumbing --------------------------------------------------
  let currentTab: 'status' | 'build' | 'recruit' = 'status';
  function applyTab(): void {
    statusTabBtn.className = currentTab === 'status' ? 'su-city-panel-tab su-active' : 'su-city-panel-tab';
    buildTabBtn.className = currentTab === 'build' ? 'su-city-panel-tab su-active' : 'su-city-panel-tab';
    recruitTabBtn.className = currentTab === 'recruit' ? 'su-city-panel-tab su-active' : 'su-city-panel-tab';
    statusTab.style.display = currentTab === 'status' ? '' : 'none';
    buildTab.style.display = currentTab === 'build' ? '' : 'none';
    recruitTab.style.display = currentTab === 'recruit' ? '' : 'none';
  }
  statusTabBtn.addEventListener('click', () => { currentTab = 'status'; applyTab(); });
  buildTabBtn.addEventListener('click', () => { currentTab = 'build'; applyTab(); });
  recruitTabBtn.addEventListener('click', () => { currentTab = 'recruit'; applyTab(); });

  // Live state -----------------------------------------------------
  let renderedSettlementId: number | null = null;
  let currentSettlementId: SettlementId | null = null;
  let currentPlayerFactionId: FactionId | null = null;

  // Button click handlers ------------------------------------------
  repairBtn.addEventListener('click', () => {
    if (currentSettlementId === null || currentPlayerFactionId === null) return;
    callbacks.onIssue({
      type: 'repair_settlement',
      tick: 0,
      playerFactionId: currentPlayerFactionId,
      settlementId: currentSettlementId,
    });
  });
  cancelBtn.addEventListener('click', () => {
    if (currentSettlementId === null || currentPlayerFactionId === null) return;
    callbacks.onIssue({
      type: 'cancel_repair_settlement',
      tick: 0,
      playerFactionId: currentPlayerFactionId,
      settlementId: currentSettlementId,
    });
  });
  garrisonBtn.addEventListener('click', () => {
    if (currentSettlementId === null || currentPlayerFactionId === null) return;
    const settlement = renderedSettlementId !== null
      ? // Resolve latest — sync() has already validated.
        undefined
      : undefined;
    void settlement;
    // Provincial context resolved sim-side via province lookup by settlementId.
    // We need the provinceId though — pass it via a wrapper below.
    const provinceId = lastKnownProvinceId;
    if (provinceId === null) return;
    callbacks.onIssue({
      type: 'recruit_cohort',
      tick: 0,
      playerFactionId: currentPlayerFactionId,
      provinceId,
      cohortType: GARRISON_COHORT_TYPE,
      armyId: null,
      settlementId: currentSettlementId,
    });
  });

  // Build buttons bind lazily to the current settlementId + provinceId.
  for (const row of buildRows) {
    row.button.addEventListener('click', () => {
      if (currentSettlementId === null || currentPlayerFactionId === null) return;
      const provinceId = lastKnownProvinceId;
      if (provinceId === null) return;
      callbacks.onIssue({
        type: 'build_structure',
        tick: 0,
        playerFactionId: currentPlayerFactionId,
        provinceId,
        structureName: row.name,
        settlementId: currentSettlementId,
      });
    });
  }

  // Captured on each refresh so button handlers see fresh state.
  let lastKnownProvinceId: import('../../sim/state').ProvinceId | null = null;

  function structuralBuild(settlement: Settlement): void {
    const kindPart = settlement.kind ? ` (${settlement.kind})` : '';
    title.textContent = `Minor Settlement #${settlement.id as unknown as number}${kindPart}`;
    kindRow.textContent = settlement.kind
      ? `Kind: ${settlement.kind} (cosmetic — no mechanical effect at v0M9)`
      : '';
    kindRow.style.display = settlement.kind ? '' : 'none';
    currentTab = 'status';
    applyTab();
    lastBuildQueueHash = '';
    lastRecruitQueueHash = '';
  }

  function refresh(state: GameState, settlement: Settlement, playerFactionId: FactionId): void {
    const faction = state.factions.get(playerFactionId);
    if (!faction) return;
    const province = state.provinces.get(settlement.provinceId);
    if (!province) return;
    lastKnownProvinceId = province.id;

    // Owner + structure count line.
    const ownerName = province.ownerFactionId === null
      ? 'unowned'
      : `Faction #${province.ownerFactionId as unknown as number}`;
    const structureCap = settlement.structureCap ?? MINOR_SETTLEMENT_STRUCTURE_CAP;
    const hereCount = countStructuresHere(province, settlement.id) + countQueuedHere(province, settlement.id);
    factsRow.textContent = `Owner: ${ownerName} — Structures: ${hereCount} / ${structureCap}`;

    refreshStatusTab(state, settlement, faction);
    refreshBuildTab(state, settlement, province, faction, structureCap);
    refreshRecruitTab(state, settlement, province, faction);
  }

  function refreshStatusTab(
    state: GameState,
    settlement: Settlement,
    faction: import('../../sim/state').Faction,
  ): void {
    const pillageState = settlement.pillageState ?? null;
    const repairState = settlement.repairState ?? null;
    const pillaged = settlement.pillaged === true;

    if (pillageState != null) {
      statusCard.style.background = 'rgba(200,80,60,0.18)';
      statusLabel.textContent = 'BEING PILLAGED';
      statusLabel.style.color = '#d97070';
      const pct = Math.max(0, Math.min(100, (pillageState.daysElapsed / MINOR_SETTLEMENT_PILLAGE_DAYS) * 100));
      barFill.style.background = '#d97070';
      barFill.style.width = `${pct.toFixed(1)}%`;
      barOuter.style.display = '';
      barLabel.style.display = '';
      const remaining = Math.max(0, MINOR_SETTLEMENT_PILLAGE_DAYS - pillageState.daysElapsed);
      barLabel.textContent = `Pillage completes in ${remaining.toFixed(1)} days (drive the enemy off to reset)`;
    } else if (repairState != null) {
      statusCard.style.background = 'rgba(70,150,90,0.18)';
      statusLabel.textContent = 'REPAIRING';
      statusLabel.style.color = '#7ac490';
      const pct = Math.max(0, Math.min(100, (repairState.daysElapsed / MINOR_SETTLEMENT_REPAIR_DAYS) * 100));
      barFill.style.background = '#7ac490';
      barFill.style.width = `${pct.toFixed(1)}%`;
      barOuter.style.display = '';
      barLabel.style.display = '';
      const remaining = Math.max(0, MINOR_SETTLEMENT_REPAIR_DAYS - repairState.daysElapsed);
      const pausedNote = hostileNearby(state, settlement) ? ' — PAUSED (enemy in ZoC)' : '';
      barLabel.textContent = `Repair completes in ${remaining.toFixed(1)} days${pausedNote}`;
    } else if (pillaged) {
      statusCard.style.background = 'rgba(200,80,60,0.18)';
      statusLabel.textContent = 'PILLAGED — structures produce no income';
      statusLabel.style.color = '#d97070';
      barOuter.style.display = 'none';
      barLabel.style.display = 'none';
    } else {
      statusCard.style.background = 'rgba(122,196,144,0.14)';
      statusLabel.textContent = 'Functional';
      statusLabel.style.color = '#7ac490';
      barOuter.style.display = 'none';
      barLabel.style.display = 'none';
    }

    if (repairState != null) {
      repairBtn.style.display = 'none';
      cancelBtn.style.display = '';
      cancelBtn.disabled = false;
      actionNote.textContent = 'Cancelling forfeits the Gold already paid.';
    } else {
      repairBtn.style.display = '';
      cancelBtn.style.display = 'none';
      if (!pillaged) {
        repairBtn.disabled = true;
        repairBtn.textContent = 'Repair';
        actionNote.textContent = 'Nothing to repair.';
      } else {
        const enemyInZoc = hostileNearby(state, settlement);
        const goldOk = faction.resources.gold >= MINOR_SETTLEMENT_REPAIR_GOLD_COST;
        if (enemyInZoc) {
          repairBtn.disabled = true;
          repairBtn.textContent = 'Repair (blocked)';
          actionNote.textContent = 'Enemy Army in ZoC — repair unavailable.';
        } else if (!goldOk) {
          repairBtn.disabled = true;
          repairBtn.textContent = `Repair (${MINOR_SETTLEMENT_REPAIR_GOLD_COST} G)`;
          actionNote.textContent = `Not enough Gold (need ${MINOR_SETTLEMENT_REPAIR_GOLD_COST}).`;
        } else {
          repairBtn.disabled = false;
          repairBtn.textContent = `Repair (${MINOR_SETTLEMENT_REPAIR_GOLD_COST} G)`;
          actionNote.textContent = `Takes ~${MINOR_SETTLEMENT_REPAIR_DAYS} days; pauses if an enemy enters ZoC.`;
        }
      }
    }
  }

  function refreshBuildTab(
    state: GameState,
    settlement: Settlement,
    province: Province,
    faction: import('../../sim/state').Faction,
    structureCap: number,
  ): void {
    const built = countStructuresHere(province, settlement.id);
    const queued = countQueuedHere(province, settlement.id);
    const total = built + queued;
    const queuedSuffix = queued > 0 ? ` (+${queued} in queue)` : '';
    buildCapNote.textContent = `Structures at this minor: ${built} / ${structureCap}${queuedSuffix}`;
    buildCapNote.style.color = total >= structureCap ? '#c47474' : '#f5eacf';

    const researched = new Set(faction.researchedKnowledge ?? []);
    const isProvinceOwner = province.ownerFactionId === faction.id;

    for (const row of buildRows) {
      const spec = STRUCTURES[row.name];
      if (!spec) continue;
      const affordable = faction.resources.gold >= spec.goldCost;
      const roomLeft = total < structureCap;
      const builtCount = province.structures.reduce(
        (n, s) => n + (s.settlementId === settlement.id && s.name === row.name ? 1 : 0),
        0,
      );
      const isQueued = province.buildQueue.some((q) => q.settlementId === settlement.id && q.name === row.name);
      const requiredKnowledge = STRUCTURE_UNLOCK_KNOWLEDGE.get(row.name);
      const researchGateMet = !requiredKnowledge || researched.has(requiredKnowledge);
      row.countBadge.textContent = `${builtCount}×`;
      row.countBadge.style.opacity = builtCount > 0 ? '0.95' : '0.5';
      row.countBadge.style.color = builtCount > 0 ? '#f4d47a' : '';

      if (!isProvinceOwner) {
        row.button.textContent = 'Locked';
        row.button.disabled = true;
        row.button.title = 'Not owner';
      } else if (!researchGateMet) {
        row.button.textContent = 'Locked';
        row.button.disabled = true;
        row.button.title = `Requires: ${requiredKnowledge}`;
      } else if (builtCount > 0) {
        row.button.textContent = 'Built';
        row.button.disabled = true;
        row.button.title = '';
      } else if (isQueued) {
        row.button.textContent = 'Queued';
        row.button.disabled = true;
        row.button.title = '';
      } else {
        row.button.textContent = 'Build';
        row.button.disabled = !affordable || !roomLeft;
        row.button.title = '';
      }
    }

    // Queue viz — only rows for this minor's in-flight orders.
    const flight = province.buildQueue.filter((q) => q.settlementId === settlement.id);
    const structureHash = flight.map((o) => o.name).join('|');
    if (structureHash !== lastBuildQueueHash) {
      buildQueueList.innerHTML = '';
      buildQueueVizRows = [];
      for (const order of flight) {
        const spec = STRUCTURES[order.name];
        if (!spec) continue;
        const rowDiv = document.createElement('div');
        rowDiv.style.margin = '4px 0';
        const labelDiv = document.createElement('div');
        labelDiv.style.fontSize = '11px';
        labelDiv.style.opacity = '0.85';
        labelDiv.style.marginBottom = '2px';
        rowDiv.appendChild(labelDiv);
        const barO = document.createElement('div');
        barO.style.height = '6px';
        barO.style.background = 'rgba(255,255,255,0.12)';
        barO.style.borderRadius = '3px';
        barO.style.overflow = 'hidden';
        const barF = document.createElement('div');
        barF.style.height = '100%';
        barF.style.background = '#f4d47a';
        barF.style.width = '0%';
        barF.style.transition = 'width 200ms linear';
        barO.appendChild(barF);
        rowDiv.appendChild(barO);
        buildQueueList.appendChild(rowDiv);
        buildQueueVizRows.push({ container: rowDiv, label: labelDiv, fill: barF, structureName: order.name, buildTimeSec: spec.buildTimeSec });
      }
      lastBuildQueueHash = structureHash;
    }
    buildQueueHeader.style.display = flight.length > 0 ? '' : 'none';
    for (let i = 0; i < buildQueueVizRows.length && i < flight.length; i++) {
      const row = buildQueueVizRows[i]!;
      const order = flight[i]!;
      const totalSec = row.buildTimeSec > 0 ? row.buildTimeSec : 1;
      const pct = Math.max(0, Math.min(100, (order.progressSec / totalSec) * 100));
      row.fill.style.width = `${pct.toFixed(1)}%`;
      const remaining = Math.max(0, totalSec - order.progressSec);
      row.label.textContent = `${row.structureName} — ${remaining.toFixed(0)}s remaining`;
    }
    void state;
  }

  function refreshRecruitTab(
    state: GameState,
    settlement: Settlement,
    province: Province,
    faction: import('../../sim/state').Faction,
  ): void {
    const spec = COHORTS[GARRISON_COHORT_TYPE];
    const currentCount = settlement.garrisonCohortIds.length;
    let queuedForThis = 0;
    for (const p of state.provinces.values()) {
      for (const o of p.recruitQueue) {
        if (o.settlementId === settlement.id) queuedForThis++;
      }
    }
    const total = currentCount + queuedForThis;
    garrisonCount.textContent = `${currentCount} / ${MINOR_GARRISON_MAX}` + (queuedForThis > 0 ? ` (+${queuedForThis} in queue)` : '');

    if (!spec) {
      garrisonBtn.disabled = true;
      garrisonBtn.textContent = 'Recruit';
      garrisonCostNote.textContent = 'Garrison cohort spec missing.';
    } else if (province.ownerFactionId !== faction.id) {
      garrisonBtn.disabled = true;
      garrisonBtn.textContent = 'Recruit';
      garrisonCostNote.textContent = 'Not owner.';
    } else if (total >= MINOR_GARRISON_MAX) {
      garrisonBtn.disabled = true;
      garrisonBtn.textContent = 'At Cap';
      garrisonCostNote.textContent = `Cap reached (${MINOR_GARRISON_MAX}).`;
    } else {
      // Manpower comes from the province capital.
      let capital: Settlement | null = null;
      for (const c of state.settlements.values()) {
        if (c.provinceId === province.id && !c.isMinor) { capital = c; break; }
      }
      const cityManpower = capital?.manpower ?? 0;
      const affordable =
        faction.resources.gold >= spec.goldCost &&
        faction.resources.iron >= spec.ironCost &&
        faction.resources.wood >= spec.woodCost &&
        faction.resources.horses >= spec.horsesCost &&
        faction.resources.supplies >= spec.suppliesCost &&
        cityManpower >= spec.figureCount;
      garrisonBtn.disabled = !affordable;
      garrisonBtn.textContent = 'Recruit';
      const bits: string[] = [];
      if (spec.goldCost > 0) bits.push(`${spec.goldCost}G`);
      bits.push(`${spec.figureCount}mp (from Capital)`);
      garrisonCostNote.textContent = `Cost: ${bits.join(' ')} — ${spec.recruitTimeSec}s. Capital manpower: ${Math.floor(cityManpower)}.`;
    }

    const flight: { p: import('../../sim/state').Province; oIndex: number; label: string; recruitTimeSec: number }[] = [];
    for (const p of state.provinces.values()) {
      for (let i = 0; i < p.recruitQueue.length; i++) {
        const o = p.recruitQueue[i]!;
        if (o.settlementId === settlement.id) {
          const cSpec = COHORTS[o.cohortType];
          if (cSpec) flight.push({ p, oIndex: i, label: o.cohortType, recruitTimeSec: cSpec.recruitTimeSec });
        }
      }
    }
    const hash = flight.map((f) => f.label).join('|');
    if (hash !== lastRecruitQueueHash) {
      recruitQueueList.innerHTML = '';
      recruitQueueVizRows = [];
      for (const f of flight) {
        const rowDiv = document.createElement('div');
        rowDiv.style.margin = '4px 0';
        const labelDiv = document.createElement('div');
        labelDiv.style.fontSize = '11px';
        labelDiv.style.opacity = '0.85';
        labelDiv.style.marginBottom = '2px';
        rowDiv.appendChild(labelDiv);
        const barO = document.createElement('div');
        barO.style.height = '6px';
        barO.style.background = 'rgba(255,255,255,0.12)';
        barO.style.borderRadius = '3px';
        barO.style.overflow = 'hidden';
        const barF = document.createElement('div');
        barF.style.height = '100%';
        barF.style.background = '#f4d47a';
        barF.style.width = '0%';
        barF.style.transition = 'width 200ms linear';
        barO.appendChild(barF);
        rowDiv.appendChild(barO);
        recruitQueueList.appendChild(rowDiv);
        recruitQueueVizRows.push({ container: rowDiv, label: labelDiv, fill: barF, structureName: f.label, buildTimeSec: f.recruitTimeSec });
      }
      lastRecruitQueueHash = hash;
    }
    recruitQueueHeader.style.display = flight.length > 0 ? '' : 'none';
    for (let i = 0; i < recruitQueueVizRows.length && i < flight.length; i++) {
      const row = recruitQueueVizRows[i]!;
      const f = flight[i]!;
      const order = f.p.recruitQueue[f.oIndex]!;
      const totalSec = row.buildTimeSec > 0 ? row.buildTimeSec : 1;
      const pct = Math.max(0, Math.min(100, (order.progressSec / totalSec) * 100));
      row.fill.style.width = `${pct.toFixed(1)}%`;
      const remaining = Math.max(0, totalSec - order.progressSec);
      row.label.textContent = `${row.structureName} — ${remaining.toFixed(0)}s remaining`;
    }
  }

  function sync(state: GameState, openSettlementId: SettlementId | null, playerFactionId: FactionId | null): void {
    if (openSettlementId === null || playerFactionId === null) {
      backdrop.style.display = 'none';
      renderedSettlementId = null;
      currentSettlementId = null;
      currentPlayerFactionId = null;
      lastKnownProvinceId = null;
      return;
    }
    const settlement = state.settlements.get(openSettlementId);
    if (!settlement || !settlement.isMinor) {
      backdrop.style.display = 'none';
      renderedSettlementId = null;
      currentSettlementId = null;
      currentPlayerFactionId = null;
      lastKnownProvinceId = null;
      return;
    }
    currentSettlementId = openSettlementId;
    currentPlayerFactionId = playerFactionId;
    const key = openSettlementId as unknown as number;
    if (key !== renderedSettlementId) {
      structuralBuild(settlement);
      renderedSettlementId = key;
    }
    refresh(state, settlement, playerFactionId);
    backdrop.style.display = 'flex';
  }

  function destroy(): void {
    backdrop.remove();
  }

  return { sync, destroy };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function countStructuresHere(province: Province, settlementId: SettlementId): number {
  let n = 0;
  for (const built of province.structures) {
    if (built.settlementId === settlementId) n++;
  }
  return n;
}

function countQueuedHere(province: Province, settlementId: SettlementId): number {
  let n = 0;
  for (const order of province.buildQueue) {
    if (order.settlementId === settlementId) n++;
  }
  return n;
}

function hostileNearby(state: GameState, settlement: Settlement): boolean {
  const province = state.provinces.get(settlement.provinceId);
  if (!province) return false;
  const ownerFactionId = province.ownerFactionId;
  let ownerTeam = -Infinity;
  if (ownerFactionId !== null) {
    const f = state.factions.get(ownerFactionId);
    if (!f) return false;
    ownerTeam = f.teamId;
  }
  const rad = 2.5;
  for (const army of state.armies.values()) {
    if (army.currentBattleId !== null) continue;
    const af = state.factions.get(army.ownerFactionId);
    if (!af) continue;
    if (ownerFactionId !== null && af.teamId === ownerTeam) continue;
    const dx = Math.abs(army.position.x - settlement.cell.x);
    const dy = Math.abs(army.position.y - settlement.cell.y);
    if (Math.max(dx, dy) <= rad) return true;
  }
  return false;
}
